import React, { Suspense } from "react";
import "./App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BasketProvider } from "./context/BasketContext";
import { PickProvider } from "./components/PickMode";
import { Layout } from "./components/Layout";
import { canSeePath, firstAllowedPath } from "./components/admin/AdminNavGroups";
import LoginPage from "./pages/LoginPage";
import GoogleCallbackPage from "./pages/GoogleCallbackPage";
import PrivateEventPage from "./pages/PrivateEventPage";
import TastingEventOrderPage from "./pages/TastingEventOrderPage";
import EventOptOutPage from "./pages/EventOptOutPage";
import PrivateEventsPage from "./pages/PrivateEventsPage";
import SignUpPage from "./pages/SignUpPage";
import DashboardPage from "./pages/DashboardPage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CellarPage from "./pages/CellarPage";
import OrdersPage from "./pages/OrdersPage";
import CellarBookPage from "./pages/CellarBookPage";
import AdviserPage from "./pages/AdviserPage";
import EventsPage from "./pages/EventsPage";
import ClubPage from "./pages/ClubPage";
import GoCardlessReturnPage from "./pages/GoCardlessReturnPage";
import PaymentReturnPage from "./pages/PaymentReturnPage";
import WelcomePage from "./pages/WelcomePage";
import MandateRequestPage from "./pages/MandateRequestPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ContentPage from "./pages/ContentPage";
import FaqPage from "./pages/FaqPage";
import CellarPlanPage from "./pages/CellarPlanPage";
import MakeCasePage from "./pages/MakeCasePage";
import GiftClubPage from "./pages/GiftClubPage";
import BasketImportPage from "./pages/BasketImportPage";
import PublicCasePage from "./pages/PublicCasePage";
import { PageTracker } from "./lib/track";

const AdminPage = React.lazy(() => import("./pages/AdminPage"));
const AdminLayout = React.lazy(() => import("./components/admin/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const Opening = () => <div className="grain flex min-h-screen items-center justify-center font-serif text-2xl text-neutral-500" data-testid="auth-loading">Opening the cellar…</div>;

/** The old Tasting notes address now lives inside Print activities (view=event → the Tasting events tile). */
function LegacyTastingNotesRedirect() {
  const { search } = useLocation();
  const view = new URLSearchParams(search).get("view") === "event" ? "events" : "notes";
  return <Navigate to={`/admin/print?view=${view}`} replace />;
}


function Protected({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { pathname, search, hash } = useLocation();
  if (user === null) return <div className="grain flex min-h-screen items-center justify-center font-serif text-2xl text-neutral-500" data-testid="auth-loading">Opening the cellar…</div>;
  if (user === false) {
    const next = pathname + search + hash;
    return <Navigate to={next && next !== "/" ? `/login?next=${encodeURIComponent(next)}` : "/login"} replace />;
  }
  return <Layout>{children}</Layout>;
}

function Public({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user === null) return <div className="grain flex min-h-screen items-center justify-center font-serif text-2xl text-neutral-500" data-testid="auth-loading">Opening the cellar…</div>;
  return <Layout>{children}</Layout>;
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <div className="grain flex min-h-screen items-center justify-center font-serif text-2xl text-neutral-500" data-testid="auth-loading">Opening the cellar…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  if (!canSeePath(user, pathname)) return <Navigate to={firstAllowedPath(user)} replace />;
  return <Suspense fallback={<Opening />}><AdminLayout>{children}</AdminLayout></Suspense>;
}

const TOAST_OPTIONS = { style: { borderRadius: 0, fontFamily: "var(--brand-body)" } };

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <BasketProvider>
          <PickProvider>
            <PageTracker />
            <Routes>
              <Route path="/auth/google" element={<GoogleCallbackPage />} />
              <Route path="/e/:token/no-more-invitations" element={<EventOptOutPage />} />
              <Route path="/e/:token" element={<PrivateEventPage />} />
              <Route path="/tasting/:token" element={<TastingEventOrderPage />} />
              <Route path="/preview/private-event/:eventId" element={<PrivateEventPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/welcome/:token" element={<WelcomePage />} />
              <Route path="/mandate/:token" element={<MandateRequestPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/" element={<Protected><DashboardPage /></Protected>} />
              <Route path="/shop" element={<Protected><ShopPage /></Protected>} />
              <Route path="/shop/:handle" element={<Protected><ProductPage /></Protected>} />
              <Route path="/cellar" element={<Protected><CellarPage /></Protected>} />
              <Route path="/orders" element={<Protected><OrdersPage /></Protected>} />
              <Route path="/cellar-book" element={<Protected><CellarBookPage /></Protected>} />
              <Route path="/adviser" element={<Protected><AdviserPage /></Protected>} />
              <Route path="/contact" element={<Protected><AdviserPage /></Protected>} />
              <Route path="/ask" element={<Navigate to="/contact" replace />} />
              <Route path="/events" element={<Protected><EventsPage /></Protected>} />
              <Route path="/private-events" element={<Protected><PrivateEventsPage /></Protected>} />
              <Route path="/club" element={<Public><ClubPage /></Public>} />
              <Route path="/gift-club" element={<Public><GiftClubPage /></Public>} />
              <Route path="/cellar-plan" element={<Public><CellarPlanPage /></Public>} />
              <Route path="/make-me-a-case" element={<Public><MakeCasePage /></Public>} />
              <Route path="/basket/import" element={<Protected><BasketImportPage /></Protected>} />
              <Route path="/case" element={<Public><PublicCasePage /></Public>} />
              <Route path="/faq" element={<Public><FaqPage /></Public>} />
              <Route path="/terms" element={<Public><ContentPage /></Public>} />
              <Route path="/admin" element={<Navigate to="/admin/customers" replace />} />
              <Route path="/admin/rotations" element={<Navigate to="/admin/lcb-duty?tab=stock" replace />} />
              <Route path="/admin/stock-flow" element={<Navigate to="/admin/stock-home" replace />} />
              <Route path="/admin/inventory" element={<Navigate to="/admin/stock-home?tab=find" replace />} />
              <Route path="/admin/tasting-notes" element={<LegacyTastingNotesRedirect />} />
              <Route path="/admin/growth/:tab" element={<AdminOnly><AdminPage /></AdminOnly>} />
              <Route path="/admin/:section" element={<AdminOnly><AdminPage /></AdminOnly>} />
              <Route path="/gocardless/return" element={<Protected><GoCardlessReturnPage /></Protected>} />
              <Route path="/payment/return" element={<Protected><PaymentReturnPage /></Protected>} />
              <Route path="/payment/cancel" element={<Navigate to="/?payment=cancelled" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-center" toastOptions={TOAST_OPTIONS} />
          </PickProvider>
          </BasketProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
